"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { cn } from "@/lib/utils";

interface ScrollCarouselProps {
  children: React.ReactNode;
  className?: string;
}

export function ScrollCarousel({ children, className }: ScrollCarouselProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 2);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 2);
  }, []);

  useEffect(() => {
    checkScroll();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", checkScroll, { passive: true });
    const observer = new ResizeObserver(checkScroll);
    observer.observe(el);
    return () => {
      el.removeEventListener("scroll", checkScroll);
      observer.disconnect();
    };
  }, [checkScroll]);

  const scroll = useCallback((direction: "left" | "right") => {
    const el = scrollRef.current;
    if (!el) return;
    const scrollAmount = el.clientWidth * 0.7;
    el.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  }, []);

  return (
    <div className="relative">
      {/* Scroll container */}
      <div
        ref={scrollRef}
        className={cn("flex gap-4 overflow-x-auto scrollbar-hide scroll-smooth", className)}
      >
        {children}
      </div>

      {/* Left arrow */}
      {canScrollLeft && (
        <button
          type="button"
          onClick={() => scroll("left")}
          className={cn(
            "hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 z-10",
            "w-10 h-10 items-center justify-center",
            "bg-white border border-neutral-200 shadow-md",
            "text-neutral-700 hover:text-neutral-900 hover:bg-neutral-50",
            "transition-colors rounded-full",
            "-translate-x-1/2",
          )}
          aria-label="이전"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
      )}

      {/* Right arrow */}
      {canScrollRight && (
        <button
          type="button"
          onClick={() => scroll("right")}
          className={cn(
            "hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 z-10",
            "w-10 h-10 items-center justify-center",
            "bg-white border border-neutral-200 shadow-md",
            "text-neutral-700 hover:text-neutral-900 hover:bg-neutral-50",
            "transition-colors rounded-full",
            "translate-x-1/2",
          )}
          aria-label="다음"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      )}
    </div>
  );
}
